import type { IconDefinition } from '@fortawesome/fontawesome-common-types';
import type { ReadonlySignal } from '@preact/signals-react';
import type { GridScreenPosition } from '@thinkalpha/ui-table-svelte/gridViewProps';
import type { Component, Snippet } from 'svelte';

export type DataTableAlign = 'left' | 'right';

export type DataTableSortDirection = 'asc' | 'desc';

export interface DataTableSortState {
    columnId: string;
    direction: DataTableSortDirection;
}

/**
 * Read side of the table. The owning model exposes rows and fetch status as
 * signals (ADR 0007); the table is pure presentation over them.
 */
export interface DataTableSource<Row> {
    rows: ReadonlySignal<readonly Row[]>;
    getRowId(row: Row): string;
    isLoading?: ReadonlySignal<boolean>;
    isError?: ReadonlySignal<boolean>;
}

/**
 * Backend-driven sorting. The table only reports `{columnId, direction}`;
 * mapping a column id to a backend sort field (e.g. an OData `$orderby`
 * expression) is the model's concern.
 */
export interface DataTableSorting {
    sort: ReadonlySignal<DataTableSortState | null>;
    /** Receives `null` when the user clears sorting (third header click). */
    setSort(next: DataTableSortState | null): void;
}

export type DataTableRowsPerPage = number | 'all';

/** Default rows per page for admin tables: rows are compact, so a denser default fits. */
export const DATA_TABLE_DEFAULT_ROWS_PER_PAGE: DataTableRowsPerPage = 25;

/** The standard selector options for admin tables. */
export const DATA_TABLE_PAGE_SIZE_OPTIONS = [10, 25, 50, 'all'] as const satisfies readonly DataTableRowsPerPage[];

/**
 * Backend-driven pagination. When the backend reports a total, provide
 * `totalCount` + `pageCount` to get numbered page buttons and an
 * "x–y of N" range. Without a total, provide `hasPreviousPage` /
 * `hasNextPage` for plain Prev/Next controls.
 */
export interface DataTablePaginationOptions {
    /** 1-based current page. */
    page: ReadonlySignal<number>;
    rowsPerPage: ReadonlySignal<DataTableRowsPerPage>;
    rowsPerPageOptions: readonly DataTableRowsPerPage[];
    totalCount?: ReadonlySignal<number>;
    pageCount?: ReadonlySignal<number>;
    hasPreviousPage?: ReadonlySignal<boolean>;
    hasNextPage?: ReadonlySignal<boolean>;
    setPage(page: number): void;
    setRowsPerPage(value: DataTableRowsPerPage): void;
}

/** Plain read-only cell. `null`/`undefined` values render as an em dash. */
export interface DataTableTextCell<Row> {
    kind: 'text';
    value(row: Row): string | number | null | undefined;
    /** Per-row classes, e.g. a status colour like `'text-green-400'`. */
    class?(row: Row): string | undefined;
    /**
     * Hover tooltip (the shared styled Tooltip). `true` shows the full
     * displayed value — pair with `truncate` for ellipsized columns whose
     * content should be readable on hover; pass a function for custom content.
     */
    tooltip?: boolean | ((row: Row) => string | undefined);
    /** Extra classes for the tooltip content, e.g. a warning colour. */
    tooltipClass?: string;
    /** Render an info icon after the text when the row has tooltip content, signalling the extra info. */
    tooltipIcon?: boolean;
    /** Ellipsize overflowing content (default `true`); `false` lets it wrap. */
    truncate?: boolean;
    /** The text the row menu's Copy Value item copies when it differs from the display (e.g. a full id behind a short one). */
    copy?: (row: Row) => string;
    /** Use tabular figures (numeric columns). */
    numeric?: boolean;
    mono?: boolean;
}

export interface DataTableTextSegment {
    text: string;
    /** Per-segment classes, e.g. a status colour like `'text-jade-450'`. */
    class?: string;
}

/**
 * Multi-value cell whose segments are independently coloured (e.g. Order
 * Debug's locate status list, "Quoted, Accepted"). An empty list renders an
 * em dash.
 */
export interface DataTableSegmentsCell<Row> {
    kind: 'segments';
    segments(row: Row): readonly DataTableTextSegment[];
    /** Separator between segments (default `', '`). */
    separator?: string;
    /** Ellipsize overflowing content (default `true`); `false` lets it wrap. */
    truncate?: boolean;
}

export type DataTableBadgeColor = 'gray' | 'blue' | 'green' | 'red' | 'orange' | 'purple' | 'cyan' | 'yellow';

export interface DataTableBadge {
    label: string;
    color?: DataTableBadgeColor;
    /** `solid` is the filled pill (type/status tags); `outline` is the bordered count chip. */
    variant?: 'solid' | 'outline';
}

/** Status/type pill cell (e.g. Global/Derived/Manual, health states). `null` renders an em dash. */
export interface DataTableBadgeCell<Row> {
    kind: 'badge';
    badge(row: Row): DataTableBadge | null;
}

/** Inline-editable numeric cell (MaskedNumber). Commits on change. */
interface DataTableNumberEditingBase<Row> {
    /** `undefined` renders an empty input (e.g. an unset limit). */
    value(row: Row): number | undefined;
    placeholder?: string;
    /** Accessible name for the input (there is no visible label). */
    ariaLabel?(row: Row): string;
    onCommit(row: Row, value: number): void;
    /** Called when the input is blanked; omit to make clearing a no-op. */
    onClear?(row: Row): void;
    /** Return an error message to reject a value; `null` accepts it. */
    validate?(row: Row, value: number): string | null;
    decimalsLimit?: number;
    disabled?(row: Row): boolean;
    /** Render as plain text instead of an input (e.g. viewer without write access). */
    readonly?(row: Row): boolean;
    /** Formats the read-only rendering; defaults to `toLocaleString` (with a `%` suffix for percent inputs). */
    format?(value: number): string;
    /** Marks the cell as edited: dirty highlight plus a reset affordance when `onReset` is set. */
    dirty?(row: Row): boolean;
    onReset?(row: Row): void;
}

export interface DataTableNumberInputCell<Row> extends DataTableNumberEditingBase<Row> {
    kind: 'number-input';
    allowNegative?: boolean;
}

/**
 * Inline-editable percentage. The input shows and edits 0–100 with a `%`
 * adornment; `value`/`onCommit`/`validate` stay in the model's domain.
 */
export interface DataTablePercentInputCell<Row> extends DataTableNumberEditingBase<Row> {
    kind: 'percent-input';
    /** The model stores fractions (0–1); the input still shows and edits 0–100. */
    fraction?: boolean;
}

/** Inline-editable text cell. Commits on Enter/blur; Escape reverts the draft. */
export interface DataTableTextInputCell<Row> {
    kind: 'text-input';
    value(row: Row): string;
    onCommit(row: Row, value: string): void;
    /**
     * Commit on every keystroke instead of Enter/blur — for form-style cells
     * whose dirty state should track live (e.g. the risk matrix). `validate`
     * still reports its message but no longer gates the commit.
     */
    commitOnInput?: boolean;
    /** Return an error message to reject a value; `null` accepts it. */
    validate?(row: Row, value: string): string | null;
    placeholder?: string;
    disabled?(row: Row): boolean;
    readonly?(row: Row): boolean;
    ariaLabel?(row: Row): string;
}

export interface DataTableSelectOption {
    value: string;
    label: string;
}

/** Read-only percentage cell; renders the number with a `%` suffix, or an em dash when nullish. */
export interface DataTablePercentCell<Row> {
    kind: 'percent';
    value(row: Row): number | null | undefined;
    /** Values are fractions (0–1) and get scaled by 100; off by default (values already read as 0–100). */
    fraction?: boolean;
    /** Fraction digits shown (default 2). */
    maximumFractionDigits?: number;
}

/** Single-choice dropdown cell (FieldSelect). */
export interface DataTableSelectCell<Row> {
    kind: 'select';
    value(row: Row): string;
    options(row: Row): readonly DataTableSelectOption[];
    onChange(row: Row, value: string): void;
    /** Render a search input in the dropdown that filters options (default `true`). */
    searchable?: boolean;
    disabled?(row: Row): boolean;
    ariaLabel?(row: Row): string;
}

/** Multi-choice dropdown cell; the popup stays open while picking and a clear button empties the selection. */
export interface DataTableMultiSelectCell<Row> {
    kind: 'multi-select';
    values(row: Row): readonly string[];
    options(row: Row): readonly DataTableSelectOption[];
    onChange(row: Row, values: string[]): void;
    /** Render a search input in the dropdown that filters options (default `true`). */
    searchable?: boolean;
    disabled?(row: Row): boolean;
    ariaLabel?(row: Row): string;
}

export interface DataTableRadioOption {
    value: string;
    label: string;
    /** Hover tooltip explaining the option. */
    tooltip?: string;
    /** Disables just this option (e.g. a level the caller may not grant). */
    disabled?: boolean;
}

/**
 * Per-row exclusive choice rendered as an inline radio group (e.g. the
 * permission level on the firm Members page).
 */
export interface DataTableRadioGroupCell<Row> {
    kind: 'radio-group';
    value(row: Row): string;
    options(row: Row): readonly DataTableRadioOption[];
    onChange(row: Row, value: string): void;
    /** Disables every option in the row's group. */
    disabled?(row: Row): boolean;
    /** Accessible name for the group. */
    ariaLabel?(row: Row): string;
}

/** Checkbox cell (boolean values, include/exclude selection columns). */
export interface DataTableCheckboxCell<Row> {
    kind: 'checkbox';
    checked(row: Row): boolean;
    onChange(row: Row, checked: boolean): void;
    disabled?(row: Row): boolean;
    ariaLabel?(row: Row): string;
}

/** Toggle cell (zag Switch). */
export interface DataTableSwitchCell<Row> {
    kind: 'switch';
    checked(row: Row): boolean;
    onChange(row: Row, checked: boolean): void;
    disabled?(row: Row): boolean;
    /** Visible label next to the control; clicking it toggles too. */
    label?(row: Row): string;
    labelClass?(row: Row): string;
    ariaLabel?(row: Row): string;
}

/** One resolved icon-button action (delete, restore, …) in an `actions` cell. */
export interface DataTableAction {
    id: string;
    icon: IconDefinition;
    /** Used as aria-label and title. */
    label: string;
    onAction(): void;
    disabled?: boolean;
    variant?: 'default' | 'danger';
}

/** Right-aligned row of icon buttons. */
export interface DataTableActionsCell<Row> {
    kind: 'actions';
    actions(row: Row): readonly DataTableAction[];
}

/** Escape hatch: render an arbitrary Svelte component with the row as its prop. */
export interface DataTableComponentCell<Row> {
    kind: 'component';
    component: Component<{ row: Row }>;
    /** The text the row menu copies for this cell; without it the cell offers no copy item. */
    copy?(row: Row): string;
}

/**
 * Escape hatch: render a snippet declared in the consuming page. The snippet also receives the
 * active highlight term, so it can mark matches the way text cells do.
 */
export interface DataTableSnippetCell<Row> {
    kind: 'snippet';
    snippet: Snippet<[Row, string]>;
    /** The text the row menu copies for this cell; without it the cell offers no copy item. */
    copy?(row: Row): string;
}

/**
 * A column's cell: a fixed kind, or a function resolving the kind per row —
 * for matrix-style columns whose input type varies by row (e.g. the Trading
 * Risk editable column: switch for booleans, number input for limits).
 */
export type DataTableCellResolver<Row> = DataTableCellDef<Row> | ((row: Row) => DataTableCellDef<Row>);

export type DataTableCellDef<Row> =
    | DataTableTextCell<Row>
    | DataTableSegmentsCell<Row>
    | DataTableBadgeCell<Row>
    | DataTablePercentCell<Row>
    | DataTableNumberInputCell<Row>
    | DataTablePercentInputCell<Row>
    | DataTableTextInputCell<Row>
    | DataTableSelectCell<Row>
    | DataTableMultiSelectCell<Row>
    | DataTableRadioGroupCell<Row>
    | DataTableCheckboxCell<Row>
    | DataTableSwitchCell<Row>
    | DataTableActionsCell<Row>
    | DataTableComponentCell<Row>
    | DataTableSnippetCell<Row>;

/** Functional column: a stable id and the cell kind that reads the row (ADR 0014 `columns`). */
export interface DataTableColumn<Row> {
    /** Stable id; keys display, layout state, sorting, filtering and menus. */
    id: string;
    cell: DataTableCellResolver<Row>;
}

/** Header label and alignment for one column (ADR 0014 `columnDisplay`). */
export interface DataTableColumnDisplay {
    id: string;
    /** Translated header label. Empty string for icon-only columns (set `headerAriaLabel`). */
    label: string;
    headerAriaLabel?: string;
    align?: DataTableAlign;
}

export type DataTableColumnPin = 'left' | 'right';

/**
 * Seed for the table's in-memory column layout, keyed by column id; the display order is the
 * `columns` order. Unlisted columns are visible and unsized. Unsized columns autosize to their
 * content once data paints; user resizes and reorders are kept in memory only.
 */
export interface DataTableInitialColumnState {
    id: string;
    /** Default `true`. */
    visible?: boolean;
    pinned?: DataTableColumnPin;
    /** Committed width in pixels; omit to autosize. */
    width?: number;
}

/** One entry of the table's current column layout, in display order. */
export interface DataTableColumnState {
    id: string;
    visible: boolean;
    pinned?: DataTableColumnPin;
    width?: number;
}

/**
 * Opt-in persistence for the table's column layout (see `createPersistedColumnLayout`). The
 * table reads it once on mount, writes after every user layout change, and clears its columns
 * when the layout is back at the seed. Without a store the layout is temporary.
 */
export interface DataTableColumnLayoutStore {
    read(): readonly DataTableColumnState[] | null;
    /** Persists the table's layout. Stored entries for columns the table does not show are kept. */
    write(columns: readonly DataTableColumnState[]): void;
    /**
     * Records that the table is back at its seed: `defaults` are its current columns as the seed has
     * them. Other stored columns are kept (with the defaults as order anchors) and `forget` ids are
     * dropped; the store empties when nothing else remains.
     */
    clear(defaults: readonly DataTableColumnState[], forget?: readonly string[]): void;
}

/** What the page's filter editor snippet receives for the column whose filter button was pressed. */
export interface DataTableFilterEditorContext {
    columnId: string;
    /** The column's header cell, for anchoring the editor. */
    anchor: HTMLElement;
    close: () => void;
}

/**
 * Per-column filtering (ADR 0014 Column Filter Contract). The table renders the header affordance
 * and the active indicator and hosts the page's editor; the page owns the filter state, applies it
 * to its rows, and passes match highlighting back through `columnHighlight`.
 */
export interface DataTableColumnFilters {
    filteredColumnIds: ReadonlySignal<ReadonlySet<string>>;
    editor: Snippet<[DataTableFilterEditorContext]>;
}

export interface DataTableCellContextMenuRequest<Row> {
    kind: 'cell';
    row: Row;
    columnId: string;
    position: GridScreenPosition;
}

export interface DataTableHeaderContextMenuRequest {
    kind: 'header';
    /** `null` on the empty header space past the last column. */
    columnId: string | null;
    position: GridScreenPosition;
}

export interface DataTableEmptyContextMenuRequest {
    kind: 'empty';
    position: GridScreenPosition;
}

export type DataTableContextMenuRequest<Row> =
    | DataTableCellContextMenuRequest<Row>
    | DataTableHeaderContextMenuRequest
    | DataTableEmptyContextMenuRequest;
